import { useAuthStore } from '@shared/stores/authStore';

/** GET /api/v1/zendesk/messaging-jwt — path relativo a VITE_API_WH (ya incluye /api/v1/). */
const ZENDESK_MESSAGING_JWT_PATH = 'zendesk/messaging-jwt';

/** Evita repetir 404 en cada intento de auth durante la misma sesión de pestaña. */
let messagingJwtEndpointMissing = false;

function logoutZendeskMessenger(): void {
  if (typeof window.zE !== 'function') {
    return;
  }

  try {
    window.zE('messenger', 'logoutUser');
  } catch {
    // ignore
  }
}

function buildMessagingJwtUrl(): string {
  const base = import.meta.env.VITE_API_WH || '';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  const path = ZENDESK_MESSAGING_JWT_PATH.replace(/^\//, '');
  return `${normalizedBase}${path}`;
}

/**
 * Pide JWT firmado al backend. null si el endpoint no existe o falla.
 * Usa fetch directo para no disparar logout global del interceptor en 401 de prueba.
 */
async function fetchZendeskMessagingJwtInternal(): Promise<string | null> {
  if (messagingJwtEndpointMissing) {
    return null;
  }

  const token = useAuthStore.getState().token;
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(buildMessagingJwtUrl(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      cache: 'no-store',
    });

    if (response.status === 404 || response.status === 501) {
      messagingJwtEndpointMissing = true;
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as { jwt?: string; token?: string };
    return data.jwt ?? data.token ?? null;
  } catch {
    return null;
  }
}

export async function fetchZendeskMessagingJwt(): Promise<string | null> {
  return fetchZendeskMessagingJwtInternal();
}

/** Widget Classic: pre-rellena nombre e id de usuario (sin email). */
export function identifyZendeskVisitor(name: string, userId: number): void {
  if (typeof window.zE !== 'function') {
    return;
  }

  const displayName = name.trim() || `Usuario ${userId}`;
  const payload = {
    name: displayName,
    externalId: String(userId),
  };

  try {
    window.zE(function () {
      window.zE?.identify?.(payload);
    });
  } catch {
    // ignore
  }
}

function loginZendeskWithMessengerJwt(onLoginError: () => void, name: string, userId: number): boolean {
  if (typeof window.zE !== 'function') {
    return false;
  }

  try {
    // Evita 409 Conflict si quedó sesión anónima u otro usuario en el messenger
    logoutZendeskMessenger();

    window.zE(
      'messenger',
      'loginUser',
      (callback: (jwt: string) => void) => {
        void fetchZendeskMessagingJwtInternal().then((jwt) => {
          if (jwt) {
            callback(jwt);
          }
        });
      },
      (error: unknown) => {
        if (error) {
          onLoginError();
          identifyZendeskVisitor(name, userId);
        }
      },
    );

    return true;
  } catch {
    return false;
  }
}

export type ZendeskAuthTrigger = 'immediate' | 'script_load' | 'timeout' | 'unknown';

export type ZendeskAuthOptions = {
  trigger?: ZendeskAuthTrigger;
  userId: number;
};

/**
 * Intenta Messaging JWT; si no hay API o falla, mantiene identify como hasta ahora.
 */
export async function authenticateZendeskVisitor(name: string, options: ZendeskAuthOptions): Promise<void> {
  if (typeof window.zE !== 'function') {
    return;
  }

  const { userId } = options;
  const jwt = await fetchZendeskMessagingJwtInternal();

  if (jwt) {
    const usedMessenger = loginZendeskWithMessengerJwt(
      () => {
        identifyZendeskVisitor(name, userId);
      },
      name,
      userId,
    );

    if (usedMessenger) {
      return;
    }
  }

  identifyZendeskVisitor(name, userId);
}
